import type { Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { useActiveBundle } from '../../../../context/active-bundle';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import { GlobalCSSVariable, useThemeContext } from '../../../../context/theme';
import { isEmptyString } from '@/utils/string';
import type { AppBundle } from '../../../../utils/app-bundles';
import type { NavItem } from '../main-navigation/routes';
import { isNavItemActive } from '../main-navigation/routes';
import { useFilterWorkspacesByName, useWorkspaceCount } from '../../../../features/workspaces/hooks';
import type { MergedWorkspace } from '../../../../features/workspaces';

import { getWorkspaceSettingsLink } from '@/hooks/get-workspace-settings-link';
import { Heading, Icon, Paragraph, Search, Box, Stack, Truncated } from '@customerio/pluma-components/react';
import { WorkspaceDot } from '../workspace-dot';
import { appendMasqParam } from '@/utils/masq';

// TODO: Much of this code has been copied from the main nav, with minor changes.
// This needs to be refactored to actually share code and reduce repetition.

function LinkItem(props: {
  item: NavItem;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => unknown;
  activeApp: AppBundle | null;
}) {
  const activeRoute = useStore(activeRouteStore);

  if (props.item.href != null) {
    return (
      <a
        className={`main-nav__main-link ${
          isNavItemActive(props.item, activeRoute) ? 'main-nav__main-link--active' : ''
        }`}
        href={appendMasqParam(props.item.href)}
        onClick={(event) => props.onClick(event, props.item)}
      >
        {props.item.iconName != null ? (
          <div className="main-nav__main-link-icon">
            <Icon name={props.item.iconName} size="md" />
          </div>
        ) : null}
        <div className="main-nav__main-link-label">{props.item.label}</div>
      </a>
    );
  }

  return <div></div>;
}

function WorkspaceLinkItem({
  workspace,
  onClick,
  currentWorkspaceRef,
}: {
  workspace: MergedWorkspace;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem, opts?: TargetTransitionOptions) => unknown;
  currentWorkspaceRef: Ref<HTMLAnchorElement> | null;
}) {
  const activeRoute = useStore(activeRouteStore);
  const workspaceSettingsLink = getWorkspaceSettingsLink();

  const navItem: NavItem = {
    id: `workspace_settings_${workspace.id}`,
    label: workspace.name,
    href: workspaceSettingsLink(workspace.id),
  };

  return (
    <a
      className={`main-nav__main-link ${isNavItemActive(navItem, activeRoute) ? 'main-nav__main-link--active' : ''}`}
      href={navItem.href}
      onClick={(event) => onClick(event, navItem)}
      ref={currentWorkspaceRef}
    >
      {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}

      <Truncated as="h3" tooltipPlacement="right" weight="regular">
        {workspace.name}
      </Truncated>
    </a>
  );
}

function SettingsSideNavWorkspacesSection({
  onClick,
}: {
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem, opts?: TargetTransitionOptions) => unknown;
}) {
  const currentWorkspace = useCurrentWorkspace();

  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState<string>('');
  const workspaceCountQuery = useWorkspaceCount();
  const filteredWorkspacesQuery = useFilterWorkspacesByName(workspaceSearchQuery);
  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const currentWorkspaceElementRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  }, []);

  return (
    <Stack mt="300" flexGrow="1">
      {allWorkspacesCount > 3 ? (
        <Search
          value={workspaceSearchQuery}
          onSearchInput={setWorkspaceSearchQuery}
          onSearch={setWorkspaceSearchQuery}
          shouldSearchOnClear={true}
          shouldSearchOnInput={true}
          placeholder="Search&hellip;"
          autoFocus={true}
          label="Workspace settings"
          mx="200"
          mb="300"
          icon={null}
        />
      ) : null}
      <Box overflowY="auto" flexGrow="1" h="0" px="200">
        {workspaceData.length > 0 ? (
          workspaceData.map((w: MergedWorkspace) => (
            <WorkspaceLinkItem
              key={w.id}
              workspace={w}
              onClick={onClick}
              currentWorkspaceRef={w.id === currentWorkspace?.id ? currentWorkspaceElementRef : null}
            />
          ))
        ) : (
          <Paragraph variant="secondary">No results found</Paragraph>
        )}
      </Box>
    </Stack>
  );
}

export function SettingsNavigation() {
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const themeContext = useThemeContext();

  const onClickNavItem = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItem,
    opts?: TargetTransitionOptions,
  ) => {
    event.preventDefault();

    if (item.href == null) {
      return;
    }

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', opts);
  };

  const items: NavItem[][] = [
    [
      {
        id: 'account_settings',
        label: 'Account Settings',
        href: '/settings',
        iconName: 'company',
        activeExceptUrls: ['/settings/personal'],
      },
      {
        id: 'personal_settings',
        label: 'Personal Settings',
        href: '/settings/personal',
        iconName: 'workspaces',
      },
    ],
  ];

  const navItemGroups = items.map((group, i) => {
    const navItems = group.map((item) => {
      return <LinkItem key={item.id} item={item} onClick={onClickNavItem} activeApp={activeApp} />;
    });

    return (
      <div key={`group_${i}`} className="main-nav__main-group">
        {navItems}
      </div>
    );
  });

  useEffect(() => {
    themeContext.setVariable(GlobalCSSVariable.MainNavWidth, `226px`);
  }, []);

  return (
    <div className="main-nav-container main-nav-styles">
      <div className="main-nav">
        <div className="main-nav__body">
          <div className="main-nav__main">
            <Heading level="2" color="bold" text="product-h3" px="200" py="200">
              Settings
            </Heading>

            {navItemGroups}
            <SettingsSideNavWorkspacesSection onClick={onClickNavItem} />
          </div>
        </div>
      </div>
    </div>
  );
}
